import React, { Dispatch, SetStateAction } from "react";

import { StatusQuickFilters } from "./StatusQuickFilters";
import { QuickFilterBar, type QuickFilterChipsProps } from "../QuickFilterBar";
import type { StatusFilterConfig } from "./filterPanel";
import { moduleListToolbarSx } from "./moduleListPresentation";
import { productionToolbarSx } from "../toolbarStyles";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  Box,
  Divider,
  IconButton,
  ListSubheader,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  GridCsvExportMenuItem,
  GridFilterItem,
  GridPrintExportMenuItem,
  GridRowSelectionModel,
  GridToolbarContainer,
  GridToolbarContainerProps,
  GridToolbarFilterButton,
  gridFilterModelSelector,
  useGridApiContext,
  useGridSelector,
} from "@mui/x-data-grid-pro";
import { useNavigate } from "react-router-dom";

import { CreateFab, SearchButton, SearchFab, getListToolbarFilterButtonSx } from "../Buttons";
import { ActionMenu, ActionMenuEntry } from "./ActionMenu";
import { ActionType, Actions } from "./DataGrid";
import { DisplayPreferences } from "./DisplayPreferences";
import { DisplaySettingsMenuItem, GridToolbarExportContainer } from "./GridToolbarExport";
import { useListingCreateTarget } from "./ListingRouteBase";
import { SearchField } from "./SearchField";
import { CreateMenu, type CreateMenuSection } from "./CreateMenu";
import { LIST_TOOLBAR_HALF_GAP } from "../dropdownMenuStyles";
import { useDataGridStore } from "../../store/DataGrid";
import { listToolbarContainerSx } from "../../utils/dataGridSx";
import { isAppliedFilterItem } from "./serverFilter";

function isSectionMenu(value: React.ReactNode | CreateMenuSection[]): value is CreateMenuSection[] {
  return Array.isArray(value) && value.every(section => section != null && typeof section === "object" &&
    "label" in section && "items" in section && Array.isArray(section.items));
}

/**
 * A list grid's FILTERS button (Pro 6.9 grid context): teal while THIS grid's Filters panel
 * holds a row that narrows it (serverFilter.ts — "is empty" counts, an unpicked "is any of"
 * does not), grey otherwise. The detail-tab grid's is PremiumListToolbar `FiltersButton`.
 *
 * It used to read the app-wide store's `filterActive`, which each click on FILTERS toggled:
 * grey while a filter was applied after a second click, and still teal on the next list
 * after a filter elsewhere (A794). The store flag still drives the empty-state wording.
 * Pass what the toolbar passed GridToolbarFilterButton (its ref anchors the panel).
 */
export const ListFiltersButton = React.forwardRef<HTMLButtonElement, Record<string, any>>(
  function ListFiltersButton(props, ref) {
    const { filtersStayActive, ...buttonProps } = props;
    const apiRef = useGridApiContext();
    const model = useGridSelector(apiRef, gridFilterModelSelector);
    const active = Boolean(filtersStayActive) || (model?.items ?? []).some((i) => i.field !== "q" && isAppliedFilterItem(i));
    return (
      <GridToolbarFilterButton
        ref={ref}
        {...buttonProps}
        data-filters-active={active ? "true" : "false"}
        sx={getListToolbarFilterButtonSx(active)}
      />
    );
  }
);

export const GridToolbarExport = ({
  csvOptions,
  printOptions,
  setAction,
  // destructured so the node is rendered as a child of the menu (and does NOT
  // ride `...other` onto the kebab button's DOM element).
  //
  // The param list is TYPED below rather than left to inference. This component
  // was untyped, so TS derived its props from the destructure and made every
  // name REQUIRED: adding `settingsMenuExtras` bare broke the four toolbars
  // that render <GridToolbarExport> directly (documents, groups, jobs,
  // workorder) with tsc 78 -> 82, and defaulting it to `undefined` instead
  // inferred the TYPE as `undefined` and broke the consumers that do pass a
  // node (78 -> 83). An explicit optional ReactNode is the fix.
  settingsMenuExtras,
  ...other
}: {
  csvOptions?: any;
  printOptions?: any;
  setAction?: any;
  settingsMenuExtras?: React.ReactNode;
  [x: string]: any;
}) => (
  <GridToolbarExportContainer {...other}>
    <ListSubheader className='list-subheader-item'>
      Export
    </ListSubheader>
    <GridCsvExportMenuItem options={csvOptions} />
    <GridPrintExportMenuItem options={printOptions} />
    <Divider />
    <ListSubheader
      className='list-subheader-item'
    >
      Settings
    </ListSubheader>
    {/* Shared with the Premium detail-tab ⋮ (H233); it consumes `hideMenu`,
        so the kebab closes behind the dialog. */}
    <DisplaySettingsMenuItem onOpen={() => setAction(Actions.Settings)} />
    {/* Module-supplied items extend the Settings section here, immediately
        after Display. Rendered as a direct child of the menu so
        GridToolbarExportContainer's cloneElement injects `hideMenu` — a
        consumer item should accept `hideMenu?: () => void` and call it so the
        kebab closes on click. */}
    {settingsMenuExtras}
  </GridToolbarExportContainer>
);

export interface AdvancedSearchToolbarProps extends GridToolbarContainerProps {
  /** Keep FILTERS active while this module's Quick Filters row is visible. */
  filtersStayActive?: boolean;
  presentation?: "moduleList" | "production";
  /** The grid's Default status filter (AeronetDataGrid `statusFilterOptions`): its Quick Filters row. */
  statusFilter?: StatusFilterConfig;
  /** The list's own Quick Filters (AeronetDataGrid `quickFilters`): drawn in place of the status chips. */
  quickFilters?: QuickFilterChipsProps;
  action: ActionType;
  actions?: ActionMenuEntry[];
  clearSearch: () => void;
  csvOptions: any;
  defaultItems: GridFilterItem[];
  enableAdvancedSearch?: boolean;
  enableCreate?: boolean;
  onCreate?: () => void;
  enableDelete?: boolean;
  enableFilter?: boolean;
  exportState: () => void;
  onChange: () => void;
  onChangeSearchField: (e: React.MouseEvent<HTMLLIElement, MouseEvent>) => void;
  onDelete?: (GridRowSelectionModel) => void; // TODO generic
  printOptions: any;
  quickSearchItems?: { [key: string]: unknown }[];
  selectionModel: GridRowSelectionModel;
  setAction: (action: string) => void;
  setSelected?: (arg0: any) => void; // TODO generic
  setSelectionModel: Dispatch<SetStateAction<GridRowSelectionModel>>;
  value: string;
  setFilterButtonEl: React.Dispatch<
    React.SetStateAction<HTMLButtonElement | null>
  >;
  getLocalStorageKey?: (userId: number) => string;
  currentUserId: number;
  name:string;
  setAdvanceSearchClicked: React.Dispatch<React.SetStateAction<boolean>>;
  advanceSearchClicked: boolean;
  setFilterObj:React.Dispatch<React.SetStateAction<GridFilterItem | undefined>>;
  /**
   * Optional trailing slot rendered at the end of the toolbar row, just
   * before the SEARCH button (which stays in its canonical right-most
   * position, next to the kebab). Use for page-specific controls such as
   * a date picker.
   */
  toolbarExtras?: React.ReactNode;
  /**
   * Optional slot rendered INSIDE the kebab (⋮) menu, in the "Settings"
   * section, immediately after the built-in "Display" item. Use for
   * module-level settings entry points that belong with Display rather than on
   * the toolbar row itself (e.g. the flights "Types and Tabs" tab picker).
   *
   * Opt-in: when omitted the Settings section is exactly as before. The node is
   * rendered as a direct child of the menu, so
   * `GridToolbarExportContainer` clones it with `hideMenu: () => void` —
   * a consumer item should accept `hideMenu` and call it on click so the popup
   * closes, and must NOT forward it to a DOM element.
   */
  settingsMenuExtras?: React.ReactNode;
  /**
   * Optional custom create menu node (e.g. CreateMenuOption).
   * When provided and enableCreate is true, rendered in place of the default CreateFab.
   */
  createMenu?: React.ReactNode | CreateMenuSection[];
}

/**
 * @figma Pattern 150 · List Toolbar
 * A toolbar with a button missing is this component with a prop off, not a new
 * component. Figma element -> prop (set via AeronetDataGrid):
 *   CreateFab            -> enableCreate (createMenu replaces the FAB)
 *   Actions              -> actions (hidden when empty)
 *   Search Field         -> quickSearchItems (the field selector)
 *   Filter               -> enableFilter
 *   Search               -> enableAdvancedSearch (SearchButton, colour owned there)
 *   3 Dot Dropdown       -> always shown; settingsMenuExtras adds items
 *   (Sample chip slot)   -> toolbarExtras
 * See docs/design-system.md.
 */
export function AdvancedSearchToolbar(props: AdvancedSearchToolbarProps) {
  const {
    action,
    actions,
    clearSearch,
    csvOptions,
    defaultItems,
    enableAdvancedSearch,
    enableCreate,
    onCreate,
    enableDelete,
    enableFilter,
    exportState,
    onChange,
    onDelete,
    printOptions,
    quickSearchItems,
    selectionModel,
    setAction,
    setSelected,
    setSelectionModel,
    setFilterButtonEl,
    value,
    getLocalStorageKey,
    currentUserId,
    name,
    setFilterObj,
    setAdvanceSearchClicked,
    // unused here, destructured so it stops riding `...other` onto the DOM
    advanceSearchClicked,
    toolbarExtras,
    createMenu,
    // destructured so it reaches <GridToolbarExport> explicitly rather than via
    // `...other` (which is also spread onto GridToolbarContainer's DOM node).
    settingsMenuExtras,
    presentation,
    statusFilter,
    quickFilters,
    filtersStayActive,
    ...other
  } = props;
  const moduleList = presentation === "moduleList";
  let navigate = useNavigate();
  // The FAB's target is NOT a bare relative "create": see ListingRouteBase.tsx.
  // A listing that sits under a mode segment resolves a relative navigate one
  // level too deep and lands on a route that does not exist (blank page under a
  // working nav bar). Modules with listing modes declare their base; everyone
  // else still gets the relative "create" they always had.
  const createTarget = useListingCreateTarget();
  const apiRef = useGridApiContext();
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down("md"));
  const {
    setAdvanceSearchTriggered,
    advanceSearchTriggered,
    filterActive,
    setFilterActive,
    setAdvanceSearch,
    resetQuickFilter
  } = useDataGridStore();

  return (
    <>
    <GridToolbarContainer sx={[listToolbarContainerSx, ...(moduleList ? [moduleListToolbarSx] : []), ...(presentation === "production" ? [productionToolbarSx] : [])]} {...other}>
      <>
        {selectionModel &&
        selectionModel.length > 0 &&
        onDelete &&
        enableDelete ? (
          <IconButton
            aria-label="delete"
            onClick={() => {
              onDelete(selectionModel);
              setSelectionModel([]);
              for (const rowId of selectionModel) {
                apiRef.current.updateRows([{ id: rowId, _action: "delete" }]);
              }
            }}
            sx={{ ml: 1.5 }}
          >
            <DeleteIcon />
          </IconButton>
        ) : (
          <>
            {enableCreate ? (
              createMenu ? (
                isSectionMenu(createMenu) ? <CreateMenu sections={createMenu} /> : createMenu
              ) : (
                <CreateFab
                  onClick={onCreate ? onCreate : () => navigate(createTarget)}
                  size="small"
                />
              )
            ) : (
              <></>
            )}
          </>
        )}
        <ActionMenu
          actions={actions}
          setAction={setAction}
          setSelected={setSelected}
          setSelectionModel={setSelectionModel}
        />
        <SearchField
          presentation={moduleList ? "moduleList" : undefined}
          value={value}
          onChange={onChange}
          clearSearch={clearSearch}
          items={quickSearchItems}
        />
        {/* FILTERS: lit by this grid's own filter model, on every list (the Tools list included) */}
        {enableFilter ? (
          <Box
            onClick={() => {
              setFilterActive(!filterActive);
              if(advanceSearchTriggered){
                  setAdvanceSearchTriggered(false);
                setAdvanceSearchClicked(false)
                setAdvanceSearch(name,[])
                }
            }}
          >
            <ListFiltersButton
              filtersStayActive={filtersStayActive}
              ref={setFilterButtonEl}
              aria-controls="options-menu"
              aria-haspopup="true"
              disableHoverListener = {true}
            />
          </Box>
        ) : (
          <></>
        )}
        <Box sx={{ flex: 1 }} />
        {toolbarExtras ? (
          <Box sx={{ display: "flex", alignItems: "center", mr: { xs: 1, sm: LIST_TOOLBAR_HALF_GAP } }}>
            {toolbarExtras}
          </Box>
        ) : null}
        <>
          {isSmall ? (
            <>
              {enableAdvancedSearch ? (
                <SearchFab
                  onClick={() => {
                    props.setAction(Actions.AdvancedSearch);
                  }}
                />
              ) : null}
            </>
          ) : (
            <>
              {enableAdvancedSearch ? (
                <>
                  <SearchButton
                    appearance={moduleList || presentation === "production" ? "primary" : undefined}
                    active={advanceSearchTriggered}
                    onClick={async () => {
                      props.setAction(Actions.AdvancedSearch);
                      if (!advanceSearchTriggered && getLocalStorageKey) {
                        const localStorageKey =
                          getLocalStorageKey(currentUserId);
                        setFilterActive(false);
                        setAdvanceSearchTriggered(true);
                        resetQuickFilter();
                        setFilterObj(undefined);
                        localStorage.setItem(
                          localStorageKey + "advanceSearch",
                          "true"
                        );
                        // `defaultItems` is typed required but a caller that
                        // does not pass `advancedSearchProps` (e.g. Flights'
                        // list, until now) leaves it undefined at runtime —
                        // DataGrid Pro's internal sanitizeFilterModel calls
                        // `.length` on `items` unconditionally and crashes the
                        // whole grid. `?? []` is the honest fallback: no
                        // default filters, not "search is broken".
                        apiRef.current.setFilterModel(
                          { items: defaultItems ?? [] },
                          "restoreState"
                        );
                      }
                    }}
                  />
                </>
              ) : (
                <></>
              )}

              <GridToolbarExport
                csvOptions={csvOptions}
                printOptions={printOptions}
                setAction={setAction}
                settingsMenuExtras={settingsMenuExtras}
                {...other}
              />
            </>
          )}
        </>
        {action === Actions.Settings ? (
          <DisplayPreferences
            onSubmit={exportState}
            open={true}
            onClose={() => {
              setAction(Actions.None);
            }}
          />
        ) : (
          <></>
        )}
      </>
    </GridToolbarContainer>
    {/* Pattern 180 under the toolbar: the list's own views, else the status chips where the grid
        has status options (and the viewer shows them). One row, never both. */}
    {quickFilters ? (
      <QuickFilterBar testId="list-quick-filters" {...quickFilters} />
    ) : (
      enableFilter && <StatusQuickFilters statusFilter={statusFilter} />
    )}
    </>
  );
}
